import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { User } from 'graphqlAPI'
import { ProgressEvaluation } from '../../../../components/ProgressEvaluation'
import EvaluationProgressPopover from '../../../../components/EvaluationProgressPopover/index'
import Tag from 'components/ui/tag/Tag'
import Options from './Options'
import UserProgress from './UserProgress'
import styles from './useColumns.module.scss'
import { ParticipantsColumns, prefixTable } from './constants'
import { CoursesCount } from './CoursesCount'
import { getPercentage } from 'helpers'
import { useMemo } from 'react'
import { Col } from 'react-bootstrap'

type Data = User

export function useColumns() {
  const { t } = useTranslation()
  type Column = {
    title: string
    key: ParticipantsColumns
    sorteable?: boolean
    width?: number
    hideable?: boolean
    position?: string
    render?: (...data: any) => any
  }

  const prefixHeaders = `${prefixTable}.headers`
  const prefixColumns = `${prefixTable}.columns`

  const columns: Column[] = useMemo(
    () => [
      {
        title: t(`${prefixHeaders}.name`),
        key: ParticipantsColumns.name,
        sorteable: true,
        hideable: false,
        width: 70,
        render: (_, { id, fullName }: Data) => {
          return (
            <Link to={`/configuration/user/${id}`} className={styles.link}>
              {fullName}
            </Link>
          )
        },
      },
      {
        title: t(`${prefixHeaders}.area`),
        key: ParticipantsColumns.area,
        width: 170,
        sorteable: true,
        render: (_, { area }: Data) => <span>{area?.name || '---'}</span>,
      },
      {
        title: t(`${prefixHeaders}.courses`),
        key: ParticipantsColumns.coursesCount,
        width: 70,
        sorteable: true,
        render: (_, value?: Data) => <CoursesCount value={value} />,
      },
      {
        title: t(`${prefixHeaders}.lms_profiles`),
        key: ParticipantsColumns.lmsProfiles,
        width: 30,
        sorteable: false,
        render: (_, value: Data) => (
          <div className={`${styles.profiles} row no-gutters`}>
            {value.lmsProfiles?.map((profile) => (
              <Col key={profile.code} xs="auto">
                <div className="text-truncate m-1">
                  <Tag
                    text={profile.name}
                    color={profile.color}
                    className="d-inline"
                  />
                </div>
              </Col>
            ))}
          </div>
        ),
      },
      {
        title: t(`${prefixHeaders}.progress`),
        key: ParticipantsColumns.coursesAdvance,
        width: 60,
        sorteable: true,
        render: (_, value?: Data) =>
          (value?.allCoursesProgress && (
            <UserProgress
              id={'progress-' + value.id}
              progress={value.allCoursesProgress}
            />
          )) || <div className="text-center">---</div>,
      },
      {
        title: t(`${prefixHeaders}.progressEvaluations`),
        key: ParticipantsColumns.evaluationsApproved,
        width: 120,
        sorteable: true,
        render: (_, value: Data) => {
          const { allCoursesProgressEvaluations: progress } = value
          return (
            (progress && (
              <EvaluationProgressPopover
                progress={{
                  approved: progress.approved,
                  reproved: progress.reproved,
                }}
                total={progress.total}
              >
                <ProgressEvaluation
                  progress={progress}
                  total={progress.total}
                />
                {t(`${prefixColumns}.progressEvaluation`, {
                  count: getPercentage(progress.approved, progress.total),
                })}
              </EvaluationProgressPopover>
            )) ||
            '---'
          )
        },
      },
      {
        title: t(`${prefixHeaders}.actions`),
        key: ParticipantsColumns.action,
        width: 100,
        hideable: false,
        position: 'left',
        render: (id, user: Data) => <Options user={user} />,
      },
    ],
    [prefixColumns, prefixHeaders, t]
  )
  return columns
}
